import React, { useState } from "react";
import MUIDataTable, {
  FilterType,
  Responsive,
  MUIDataTableMeta,
} from "mui-datatables";
import {
  makeStyles,
  withStyles,
  createMuiTheme,
  MuiThemeProvider,
} from "@material-ui/core/styles";
import TableContainer from "@material-ui/core/TableContainer";
import Paper from "@material-ui/core/Paper";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import Button from "@material-ui/core/Button";

import StaffTravelItem from "./staffTravelItem.component";

import "../assets/sass/elements/staffTravelDispGrid.styles.scss";
import { ITravel, TravelPayload, IEmployeeInfo } from "../interfaces";
import { useFormContext } from "react-hook-form";
import { ActionTypes } from "../reducers/action-types";
import { CollectionActions } from "../reducers/actions";
import { Validation } from "../classes";
import { arrIndexerName } from "../utilities";
const useStyles = makeStyles({
  editIcon: {
    fontSize: "1.2rem",
  },
});

const getMuiTheme = () =>
  (createMuiTheme as any)({
    overrides: {
      MUIDataTable: {
        paper: {
          backgroundColor: "rgba(243, 243, 243, 1)",
          "@media print": {
            backgroundColor: "white",
            paddingTop: "3cm",
            paddingBottom: "3cm",
          },
        },
      },
      MUIDataTableToolbar: {
        left: {
          display: "inline-flex",
          marginLeft: "-12px",
        },
        actions: {
          "@media print": {
            display: "none",
          },
        },
      },
      MuiTypography: {
        root: {
          color: "rgba(0, 0, 0, 0.7)",
        },
      },
      MUIDataTableViewCol: {
        checkboxRoot: {
          "&&$checked": {
            color: "rgba(1, 65, 137, 1)",
          },
        },
      },
      MuiTableCell: {
        root: {
          padding: "0",
        },
        body: {
          color: "rgba(0, 0, 0, 0.7)",
        },
      },
      MUIDataTableFilter: {
        checkbox: {
          "&&$checked": {
            color: "rgba(1, 65, 137, 1)",
          },
        },
      },
      MUIDataTableSelectCell: {
        root: {
          "@media print": {
            display: "none",
          },
        },
        headerCell: {
          backgroundColor: "rgba(0, 0, 0, 0.09)",
        },
        fixedHeaderCommon: {
          backgroundColor: "transparent",
        },
      },
      MUIDataTableHeadCell: {
        fixedHeaderCommon: {
          backgroundColor: "rgba(0, 0, 0, 0.09)",
        },
        toolButton: {
          display: "inline-block",
        },
      },
      MuiButton: {
        textPrimary: {
          color: "rgba(1, 65, 137, 1)",
          "&:focus": {
            outlineWidth: 0,
          },
        },
      },
      MuiIconButton: {
        root: {
          "&:focus": {
            outlineWidth: 0,
          },
        },
      },
    },
  });

type StaffTravelDispGridProps = {
  parentId: string;
  reducer: [IEmployeeInfo, React.Dispatch<CollectionActions<ITravel>>];
  addClass?: string;
};

export default function StaffTravelDispGrid({
  parentId,
  addClass,
  reducer,
}: // dispatch,
StaffTravelDispGridProps) {
  const classes = useStyles();
  const formNames = {
    travel: "existingItems",
  };
  const DialogStyledButton = withStyles({
    root: {
      color: "#ffffff",
      backgroundColor: "#014189",
      opacity: 1,
      fontWeight: 400,
      fontSize: ".875rem",
      lineHeight: 1.5,
      display: "inline-flex",
      margin: "24px 10px 15px",
      "&:hover": {
        backgroundColor: "#346ba9",
      },
      "&:focus": {
        outlineWidth: 0,
      },
      padding: ".25rem .5rem",
      fontFamily: "inherit",
    },
    label: {
      textTransform: "capitalize",
    },
  })(Button);
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const [selectedNdx, setSelectedNdx] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<
    Partial<ITravel> | undefined
  >(undefined);
  const { control, setError } = useFormContext();

  const handleClickOpen = (index: number, items?: any[]) => {
    if (items) {
      const travel = existingItems[index];
      setSelectedNdx(index);
      setSelectedItem(travel);
      setOpen(true);
    }
  };

  const [employee, dispatch] = reducer;

  const { existingItems } = employee.travelCollection;

  const handleDelete = (u: any) => {
    const rows = u as {
      data: Array<{ index: number; dataIndex: number }>;
      lookup: { [property: number]: boolean };
    };

    const indicies = rows.data.map((d) => d.dataIndex);

    const sorted = indicies.sort();

    employee.travelCollection.deleteItems(sorted);

    dispatch({ type: ActionTypes.SET_EMPLOYEE, payload: employee });
  };

  const handleSave = () => {
    const record = control.getValues({ nest: true }) as TravelPayload;

    const cr = record.existingItems[selectedNdx];

    if (Validation.hasNoValue(cr.empTripOffice)) {
      setError(arrIndexerName("existingItems", selectedNdx, "empTripOffice"), {
        type: "manual",
        message: "Legat is required",
      });
      return;
    }
    if (Validation.hasNoValue(cr.empTripStart)) {
      setError(arrIndexerName("existingItems", selectedNdx, "empTripStart"), {
        type: "manual",
        message: "Start date required.",
      });
      return;
    }
    if (
      cr.empTripEnd !== "" &&
      !Validation.isBeforeDate(cr.empTripStart, cr.empTripEnd)
    ) {
      setError(arrIndexerName("existingItems", selectedNdx, "empTripEnd"), {
        type: "manual",
        message: "cannot precede start date.",
      });
      return;
    }

    cr.empTripId = existingItems[selectedNdx].empTripId;

    employee.travelCollection.updateItem("empTripId", cr);

    dispatch({ type: ActionTypes.SET_EMPLOYEE, payload: employee });

    setOpen(false);
  };

  const columns = [
    {
      name: "parentId",
      options: {
        display: false,
        filter: false,
        viewColumns: false,
        searchable: false,
      },
    },
    {
      name: "empTripId",
      options: {
        display: false,
        filter: false,
        viewColumns: false,
        searchable: false,
      },
    },
    {
      name: "empTripOffice",
      label: "Legat",
      options: {
        display: true,
        filter: true,
        viewColumns: true,
        searchable: true,
      },
    },
    {
      name: "USStartDate",
      label: "Start Date",
      options: {
        display: true,
        filter: true,
        viewColumns: true,
        searchable: true,
      },
    },
    {
      name: "USEndDate",
      label: "End Date",
      options: {
        display: true,
        filter: true,
        viewColumns: true,
        searchable: true,
        printable: true,
      },
    },
    {
      name: "empTripEAN",
      label: "EAN",
      options: {
        display: true,
        filter: true,
        viewColumns: true,
        searchable: true,
      },
    },
    {
      name: "empTripType",
      label: "Travel Type",
      options: {
        display: true,
        filter: true,
        viewColumns: true,
        searchable: true,
        printable: true,
      },
    },
    {
      name: "empTripCost",
      label: "Cost",
      options: {},
    },
    {
      name: "IsActual",
      label: "Cost Calc",
      options: {
        setCellHeaderProps: (value: any) => ({
          className: "centered",
          style: { textAlign: "center" },
        }),
        setCellProps: (value: any) => ({ style: { textAlign: "center" } }),
        display: true,
        filter: true,
        viewColumns: true,
        searchable: true,
        printable: true,
      },
      filter: false,
    },
    {
      name: "empTripNotes",
      label: "Notes",
      options: {
        display: false,
        filter: false,
        sort: false,
        viewColumns: true,
        searchable: false,
        print: true,
      },
    },
    {
      name: "Edit",
      label: "...",
      options: {
        filter: false,
        sort: false,
        print: false,
        customBodyRender: (
          _value: any,
          tableMeta: MUIDataTableMeta,
          _updateValue: any
        ) => (
          <React.Fragment>
            <IconButton
              color="primary"
              onClick={() => {
                handleClickOpen(tableMeta.rowIndex, tableMeta.rowData);
              }}
            >
              <Tooltip title="Click to Edit...">
                <EditIcon className={classes.editIcon} />
              </Tooltip>
            </IconButton>
          </React.Fragment>
        ),
        viewColumns: false,
        searchable: false,
        setCellHeaderProps: (value: any) => ({
          style: { justifyContent: "center", textAlign: "center" },
        }),
        setCellProps: (value: any) => ({ style: { textAlign: "center" } }),
      },
    },
  ] as any;

  const options = {
    filterType: "multiselect" as FilterType,
    responsive: "scrollFullHeight" as Responsive,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 15, 100],
  };

  return (
    <React.Fragment>
      <TableContainer
        id="staffTravelGrid"
        component={Paper}
        className={`${addClass}`}
      >
        <MuiThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title={
              <Typography
                variant="h6"
                id="staffTravelGrid-title"
                component="div"
              >
                Travel
              </Typography>
            }
            data={existingItems}
            columns={columns}
            options={{ ...options, onRowsDelete: (rows) => handleDelete(rows) }}
          />
        </MuiThemeProvider>
      </TableContainer>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        PaperProps={{ variant: "outlined" }}
        className="border border-primary"
        maxWidth="md"
      >
        <DialogTitle id="form-dialog-title" className="text-primary">
          Edit Trip
        </DialogTitle>
        <DialogContent>
          <StaffTravelItem
            parentId="thisParentId"
            name={formNames.travel}
            index={selectedNdx}
            item={selectedItem}
          />
        </DialogContent>
        <DialogActions>
          <DialogStyledButton onClick={handleClose}>Cancel</DialogStyledButton>
          <DialogStyledButton onClick={handleSave}>Save</DialogStyledButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
